import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Thermometer, Plus, Trash2 } from 'lucide-react';

const ProviderWarehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API_URL}/provider/warehouses`);
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (warehouseId, warehouseName) => {
    if (!window.confirm(`Are you sure you want to delete "${warehouseName}"? This will also delete all associated MHE and manpower services.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/warehouses/${warehouseId}`);
      toast.success('Warehouse deleted successfully');
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete warehouse');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
              My Warehouses
            </h1>
            <p className="text-muted-foreground">Manage your warehouse listings and inventory</p>
          </div>
          <Button
            onClick={() => navigate('/provider/add-warehouse')}
            data-testid="add-warehouse-button"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm transition-all active:scale-95"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Add Warehouse
          </Button>
        </div>

        {warehouses.length === 0 ? (
          <Card className="bg-white border border-border rounded-sm shadow-sm p-12 text-center">
            <h3 className="font-heading text-xl font-bold text-foreground mb-2">No warehouses yet</h3>
            <p className="text-muted-foreground mb-6">Start by adding your first warehouse listing</p>
            <Button
              onClick={() => navigate('/provider/add-warehouse')}
              className="rounded-sm"
            >
              Add Warehouse
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="warehouses-grid">
            {warehouses.map((warehouse) => (
              <Card
                key={warehouse.id}
                data-testid={`warehouse-${warehouse.id}`}
                className="bg-white border border-border rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold text-foreground">{warehouse.name}</h3>
                  {warehouse.temperature_controlled && (
                    <Thermometer className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  )}
                </div>

                <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
                  <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  <span>{warehouse.location.city}, {warehouse.location.state}</span>
                </div>

                <div className="space-y-2 mb-4">
                  {warehouse.bulk_floor_sqm > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Bulk Floor:</span>
                      <span className="font-medium text-foreground">{warehouse.bulk_floor_sqm} SQM</span>
                    </div>
                  )}
                  {warehouse.racking_positions > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Racking:</span>
                      <span className="font-medium text-foreground">{warehouse.racking_positions} PP</span>
                    </div>
                  )}
                  {warehouse.variable_cbm > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Variable:</span>
                      <span className="font-medium text-foreground">{warehouse.variable_cbm} CBM</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {warehouse.storage_types.map((type) => (
                    <span key={type} className="px-2.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wide">
                      {type.replace('_', ' ')}
                    </span>
                  ))}
                </div>

                <div className="pt-4 border-t border-border flex items-center justify-between">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                    warehouse.available ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {warehouse.available ? 'Available' : 'Unavailable'}
                  </span>
                  <Button
                    onClick={() => handleDelete(warehouse.id, warehouse.name)}
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-warehouse-${warehouse.id}`}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-sm"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderWarehouses;